import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Rocket,
  Search,
  Radar,
  Brain,
  FileText,
  Library,
  Zap,
  BarChart3,
  Settings,
  Menu,
  X,
  Bell,
  Wand2,
  Globe,
  Image,
  Rss,
  Mic,
  Coins,
  Map,
  Mail,
  BookOpen,
  MessageSquare,
  Link2,
  Calendar,
  LogOut,
  CreditCard,
  Monitor,
  Users,
  Target,
  CheckCircle,
  AlertCircle,
  UserPlus,
  DollarSign,
  Bug,
  Info,
  Headphones,
  ClipboardList,
  Star,
  ShieldCheck,
  ChevronDown,
  Trash2,
  Upload,
  ExternalLink,
  ListTree,
  Newspaper,
  Lock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { supabase } from '../lib/supabase';
import { useSEO } from '../lib/useSEO';
import Footer from './Footer';
import EmailVerificationBanner from './EmailVerificationBanner';

import { FeatureKey, getFeatureRule, requiredTierLabel } from '../../supabase/functions/_shared/planFeatures';

interface LayoutProps {
  children: React.ReactNode;
}

interface SearchResult {
  id: string;
  title: string;
  type: string;
  href: string;
}

const NEW_ACCOUNT_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const SIMPLE_NAV_HREFS = ['/dashboard', '/marketing-manager', '/library', '/leads', '/email', '/scheduler'];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  useSEO(location.pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAllTools, setShowAllTools] = useState(false);
  const { user, signOut, canUse } = useAuth();
  const { brandName, isWhiteLabeled, primaryColorHex, logoAssetPath } = useTenant();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Notifications state
  interface AppNotification {
    id: string;
    category: string;
    title: string;
    body: string | null;
    link: string | null;
    severity: string;
    read_at: string | null;
    created_at: string;
  }
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    setNotifLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_notifications')
        .select('id, category, title, body, link, severity, read_at, created_at')
        .eq('recipient_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      const rows = (data ?? []) as AppNotification[];
      setNotifications(rows);
      setUnreadCount(rows.filter(n => !n.read_at).length);
    } catch {
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('admin_notifications_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'admin_notifications', filter: `recipient_user_id=eq.${user.id}` }, () => {
        loadNotifications();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, loadNotifications]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read_at);
    if (unread.length === 0) return;
    const now = new Date().toISOString();
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read_at: n.read_at || now })));
    const results = await Promise.all(unread.map((notification) => supabase.rpc('manage_admin_notification', {
      p_notification_id: notification.id,
      p_action: 'read',
    })));
    if (results.some(({ data, error }) => error || data !== true)) {
      loadNotifications();
    }
  };

  const markOneRead = async (id: string) => {
    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: n.read_at || now } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    const { data, error } = await supabase.rpc('manage_admin_notification', {
      p_notification_id: id,
      p_action: 'read',
    });
    if (error || data !== true) {
      loadNotifications();
    }
  };

  const deleteNotification = async (id: string) => {
    const target = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (target && !target.read_at) setUnreadCount(prev => Math.max(0, prev - 1));
    const { data, error } = await supabase.rpc('manage_admin_notification', {
      p_notification_id: id,
      p_action: 'delete',
    });
    if (error || data !== true) {
      loadNotifications();
    }
  };

  const openNotification = async (notification: AppNotification) => {
    if (!notification.read_at) await markOneRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
      setNotifOpen(false);
    }
  };

  const clearReadNotifications = async () => {
    const readIds = notifications.filter(n => n.read_at).map(n => n.id);
    if (readIds.length === 0) return;
    setNotifications(prev => prev.filter(n => !n.read_at));
    const results = await Promise.all(readIds.map((id) => supabase.rpc('manage_admin_notification', {
      p_notification_id: id,
      p_action: 'delete',
    })));
    if (results.some(({ data, error }) => error || data !== true)) loadNotifications();
  };

  const notifIcon = (category: string, severity: string) => {
    const cls = 'h-4 w-4 ';
    if (severity === 'error') return <AlertCircle className={cls + 'text-rose-500'} />;
    if (severity === 'warning') return <AlertCircle className={cls + 'text-amber-500'} />;
    if (severity === 'success') return <CheckCircle className={cls + 'text-emerald-500'} />;
    switch (category) {
      case 'subscriber': return <UserPlus className={cls + 'text-blue-500'} />;
      case 'payout': return <DollarSign className={cls + 'text-emerald-500'} />;
      case 'feedback': return <Bug className={cls + 'text-violet-500'} />;
      case 'affiliate': return <Users className={cls + 'text-pink-500'} />;
      case 'support': return <Headphones className={cls + 'text-teal-500'} />;
      default: return <Info className={cls + 'text-slate-400'} />;
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const notificationsDropdown = (
    <div ref={notifRef} className="relative">
      <button
        onClick={() => { setNotifOpen(o => !o); if (!notifOpen) loadNotifications(); }}
        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors relative"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {notifOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] glass-card rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-fade-in">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <p className="text-sm font-semibold text-slate-700">Notifications</p>
            <div className="flex items-center gap-3">
              {notifications.some(n => n.read_at) && (
                <button onClick={clearReadNotifications} className="text-[11px] font-medium text-slate-400 hover:text-rose-500 transition-colors">
                  Clear read
                </button>
              )}
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-[11px] font-medium text-violet-600 hover:text-violet-700">
                  Mark all read
                </button>
              )}
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifLoading ? (
              <div className="py-8 text-center text-xs text-slate-400">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-400">No notifications yet.</p>
              </div>
            ) : (
              notifications.map(n => {
                const unread = !n.read_at;
                return (
                  <div
                    key={n.id}
                    className={`group w-full flex items-start gap-3 px-4 py-3 text-left border-b border-slate-50 transition-colors ${
                      unread ? 'bg-violet-50/40 hover:bg-violet-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div
                      role={n.link ? 'button' : undefined}
                      tabIndex={n.link ? 0 : undefined}
                      onClick={() => openNotification(n)}
                      onKeyDown={(event) => {
                        if (n.link && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          openNotification(n);
                        }
                      }}
                      className={`flex items-start gap-3 flex-1 min-w-0 ${n.link ? 'cursor-pointer' : 'cursor-default'}`}
                      title={n.link ? 'Click to open' : undefined}
                    >
                      <div className="flex-shrink-0 mt-0.5">{notifIcon(n.category, n.severity)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className={`text-xs flex-1 ${unread ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>{n.title}</p>
                          {n.link && <ExternalLink className="h-3 w-3 text-slate-300 group-hover:text-violet-500 flex-shrink-0 transition-colors" />}
                        </div>
                        {n.body && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-slate-300 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                      {unread && <span className="w-2 h-2 rounded-full bg-violet-500 flex-shrink-0 mt-1.5" />}
                    </div>
                    <div className="flex-shrink-0 flex items-start gap-1 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {unread && (
                        <button
                          onClick={(event) => { event.stopPropagation(); markOneRead(n.id); }}
                          className="p-1 text-slate-300 hover:text-emerald-500 transition-colors"
                          title="Mark as read"
                        >
                          <CheckCircle className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(event) => { event.stopPropagation(); deleteNotification(n.id); }}
                        className="p-1 text-slate-300 hover:text-rose-500 transition-colors"
                        title="Delete notification"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );

  const brandColor = primaryColorHex || '#f59e0b';
  const showCustomLogo = isWhiteLabeled && logoAssetPath && logoAssetPath !== '/logo.svg';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim() || !user) {
      setSearchResults([]);
      return;
    }
    const q = searchQuery.toLowerCase();
    const results: SearchResult[] = [];
    const navResults = allNavItems
      .filter(item => item.name.toLowerCase().includes(q))
      .map(item => ({ id: item.href, title: item.name, type: 'Page', href: item.href }));
    results.push(...navResults.slice(0, 5));

    const dbSearch = async () => {
      const [briefs, assets, sequences, affiliates] = await Promise.all([
        supabase.from('research_briefs').select('id, topic').ilike('topic', `%${q}%`).eq('tenant_id', user.id).limit(3),
        supabase.from('content_assets').select('id, title').ilike('title', `%${q}%`).eq('tenant_id', user.id).limit(3),
        supabase.from('email_sequences').select('id, name').ilike('name', `%${q}%`).eq('user_id', user.id).limit(3),
        supabase.from('affiliate_links').select('id, name').ilike('name', `%${q}%`).eq('user_id', user.id).limit(3),
      ]);
      (briefs.data ?? []).forEach((b: any) => results.push({ id: b.id, title: b.topic, type: 'Research', href: '/research' }));
      (assets.data ?? []).forEach((a: any) => results.push({ id: a.id, title: a.title, type: 'Content', href: '/library' }));
      (sequences.data ?? []).forEach((s: any) => results.push({ id: s.id, title: s.name, type: 'Email', href: '/email' }));
      (affiliates.data ?? []).forEach((a: any) => results.push({ id: a.id, title: a.name, type: 'Affiliate', href: '/affiliates' }));
      setSearchResults(results.slice(0, 10));
    };
    const timer = setTimeout(() => dbSearch(), 200);
    return () => clearTimeout(timer);
  }, [searchQuery, user]);

  const navGroups: { label: string; items: { name: string; href: string; icon: React.ElementType; feature?: FeatureKey }[] }[] = [
    {
      label: 'Overview',
      items: [
        { name: 'Showcase', href: '/', icon: Rocket },
        { name: 'Dashboard', href: '/dashboard', icon: Home },
      ],
    },
    {
      label: 'Content Pipeline',
      items: [
        { name: 'Research', href: '/research', icon: Search },
        { name: 'Generate', href: '/generate', icon: FileText },
        { name: 'Content Library', href: '/library', icon: Library, feature: 'content_library' },
        { name: 'Publishing Hub', href: '/publishing', icon: Globe, feature: 'publishing_hub' },
      ],
    },
    {
      label: 'Automation',
      items: [
        { name: 'Marketing Manager', href: '/marketing-manager', icon: Brain, feature: 'marketing_manager' },
        { name: 'Pipeline Engine', href: '/automation', icon: Zap, feature: 'automation_pipeline' },
        { name: 'Blog Rotation', href: '/blog-rotation', icon: ListTree, feature: 'blog_rotation' },
        { name: 'Blog Library', href: '/blog-library', icon: Newspaper, feature: 'blog_library' },
        { name: 'Public Blog', href: '/blog', icon: ExternalLink },
        { name: 'Social Media', href: '/scheduler', icon: Calendar, feature: 'social_scheduler' },
      ],
    },
    {
      label: 'Marketing',
      items: [
        { name: 'Email Marketing', href: '/email', icon: Mail, feature: 'email_marketing' },
        { name: 'Lead Capture', href: '/leads', icon: Users },
        { name: 'Campaigns', href: '/campaigns', icon: Target, feature: 'campaigns' },
        { name: 'Lead Generation', href: '/lead-generation', icon: Radar, feature: 'lead_generation' },
        { name: 'Affiliate Portal', href: '/affiliates', icon: Link2, feature: 'affiliate_portal' },
        { name: 'Slideshow Builder', href: '/slideshow', icon: Monitor, feature: 'slideshow_builder' },
      ],
    },
    {
      label: 'Creative',
      items: [
        { name: 'Image Generator', href: '/images', icon: Image },
        { name: 'Output Showcase', href: '/output-showcase', icon: Star, feature: 'output_showcase' },
        { name: 'E-book Covers', href: '/covers', icon: BookOpen, feature: 'ebook_covers' },
        { name: 'Prompt Creator', href: '/prompt-creator', icon: Wand2, feature: 'prompt_creator' },
        { name: 'RSS Creator', href: '/rss', icon: Rss, feature: 'rss_import' },
        { name: 'Media Studio', href: '/media-studio', icon: Mic },
        { name: 'Token Usage', href: '/tokens', icon: Coins },
        { name: 'Roadmap', href: '/roadmap', icon: Map },
      ],
    },
    {
      label: 'Insights',
      items: [
        { name: 'Analytics', href: '/analytics', icon: BarChart3 },
        { name: 'Live Chat', href: '/live-chat', icon: MessageSquare },
        { name: 'Live Support', href: '/support', icon: Headphones, feature: 'live_support' },
        { name: 'Chat Inbox', href: '/admin-chat', icon: Headphones, feature: 'admin_inbox' },
        { name: 'Members', href: '/admin-members', icon: Users, feature: 'admin_members' },
        { name: 'Testimonial Approvals', href: '/admin-testimonials', icon: ShieldCheck, feature: 'testimonial_admin' },
      ],
    },
    {
      label: 'System',
      items: [
        { name: 'Setup Wizard', href: '/setup', icon: Rocket },
        { name: 'Pricing', href: '/pricing', icon: CreditCard },
        { name: 'Settings', href: '/settings', icon: Settings },
        { name: 'Tester Questionnaire', href: '/questionnaire', icon: ClipboardList, feature: 'tester_questionnaire' },
        { name: 'Tester Uploads', href: '/tester-uploads', icon: Upload, feature: 'tester_uploads' },
        { name: 'Testimonials', href: '/testimonials', icon: Star },
      ],
    },
  ];

  const allNavItems = navGroups.flatMap(g => g.items);
  const createdAtMs = user?.created_at ? new Date(user.created_at).getTime() : 0;
  const isNewAccount = createdAtMs > 0 && Date.now() - createdAtMs < NEW_ACCOUNT_WINDOW_MS;
  const isSimpleNav = isNewAccount && !showAllTools;
  const accessibleNavGroups = navGroups
    .map(g => ({ ...g, items: g.items.filter(i => !i.feature || requiredTierLabel(i.feature) || canUse(i.feature)) }))
    .filter(g => g.items.length > 0);
  const visibleNavGroups = isSimpleNav
    ? accessibleNavGroups
        .map(g => ({ ...g, items: g.items.filter(i => SIMPLE_NAV_HREFS.includes(i.href)) }))
        .filter(g => g.items.length > 0)
    : accessibleNavGroups;
  const isActive = (href: string) => location.pathname === href;

  const brandIconStyle = isWhiteLabeled
    ? { background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)` }
    : undefined;

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity lg:hidden z-40 animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200/80 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:flex lg:flex-col shadow-sm`}>
        <div className="flex items-center justify-between h-16 px-5 border-b border-slate-200/80">
          <div className="flex items-center gap-3">
            {showCustomLogo ? (
              <img src={logoAssetPath} alt={brandName} className="w-9 h-9 rounded-xl object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-lg" style={brandIconStyle ?? { background: 'linear-gradient(to bottom right, #7c3aed, #9333ea)' }}>
                <Rocket className="h-5 w-5 text-white" />
              </div>
            )}
            <span className="text-base font-bold text-slate-800 tracking-tight">{brandName}</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 mt-3 px-3 overflow-y-auto">
          {visibleNavGroups.map((group) => (
            <div key={group.label} className="mb-5">
              <p className="px-3 mb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest font-mono">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.href);
                  const locked = !!item.feature && !canUse(item.feature);
                  const lockedTier = item.feature ? requiredTierLabel(item.feature) : null;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 group ${
                          active
                            ? 'bg-violet-50 text-violet-700 font-semibold'
                            : locked
                              ? 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                              : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                        }`}
                      >
                        <item.icon className={`mr-3 h-4 w-4 transition-colors ${
                          active ? 'text-violet-600' : 'text-slate-400 group-hover:text-violet-500'
                        }`} />
                        <span className="flex-1 truncate">{item.name}</span>
                        {locked && (
                          <span
                            title={`Upgrade to ${lockedTier}`}
                            aria-label={`Upgrade to ${lockedTier}`}
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                          >
                            <Lock className="h-2.5 w-2.5" />
                            {lockedTier}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {isNewAccount && (
            <button
              onClick={() => setShowAllTools(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800 rounded-lg transition-colors"
            >
              <span>{showAllTools ? 'Hide extra tools' : 'All tools'}</span>
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showAllTools ? 'rotate-180' : ''}`} />
            </button>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200/80">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={brandIconStyle ?? { background: 'linear-gradient(to bottom right, #7c3aed, #9333ea)' }}>
              {user?.email?.[0]?.toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">
                {user?.email ?? 'Unknown'}
              </p>
            </div>
            <button
              onClick={signOut}
              className="text-slate-400 hover:text-error-500 transition-colors p-1.5 rounded-lg hover:bg-error-100"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/80 sticky top-0 z-30 hidden lg:block">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex-1 max-w-md" ref={searchRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                  placeholder="Search across your workspace..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-primary-400 focus:bg-white transition-colors"
                />
                {searchOpen && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 glass-card rounded-lg overflow-hidden shadow-xl z-50">
                    {searchResults.map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => { navigate(result.href); setSearchOpen(false); setSearchQuery(''); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                      >
                        <span className="px-1.5 py-0.5 bg-primary-50 text-primary-600 text-[10px] rounded font-mono uppercase">{result.type}</span>
                        <span className="text-sm text-slate-700 truncate flex-1">{result.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {notificationsDropdown}
          </div>
        </header>

        <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/80 lg:hidden sticky top-0 z-30">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-lg transition-colors"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              {showCustomLogo ? (
                <img src={logoAssetPath} alt={brandName} className="w-7 h-7 rounded-lg object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={brandIconStyle ?? { background: 'linear-gradient(to bottom right, #7c3aed, #9333ea)' }}>
                  <Rocket className="h-4 w-4 text-white" />
                </div>
              )}
              <span className="text-base font-bold text-slate-800">{brandName}</span>
            </div>
            {notificationsDropdown}
          </div>
        </header>

        <div className="flex-1 flex flex-col min-w-0">
          <EmailVerificationBanner />
          <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
            {children}
          </main>
          <Footer />
        </div>
      </div>

    </div>
  );
};

export default Layout;
